'use client';

import { useCallback, useEffect, useState } from 'react';
import LottieAnimation from './atoms/LottieAnimation';
import { useTheme } from '../hooks/useTheme';
import { useSplashHold } from '../contexts/SplashContext';

interface Props {
  slice?: boolean,
}

// Animated marquee that sits just under the hero banner.
//
// Two perf wins vs the previous implementation:
//   1. We render only the variant matching the active theme instead of
//      mounting both and toggling visibility with `hidden dark:block` —
//      the inactive JSON used to download and a second react-lottie
//      instance ran for nothing.
//   2. We defer mounting until after first paint via rAF + a one-tick
//      timeout, so the JSON fetch + react-lottie chunk don't compete
//      with the LCP image for main-thread time and network.
const CardsLottie = ({ slice = true }: Props) => {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [height, setHeight] = useState('30px');
  const [isAnimationReady, setIsAnimationReady] = useState(false);
  const handleAnimationReady = useCallback(() => setIsAnimationReady(true), []);
  // Deferred on purpose (see above), so it would otherwise pop in after the
  // page is revealed; keep the first-load splash up until it renders.
  useSplashHold(isAnimationReady);

  useEffect(() => {
    const updateHeight = () => {
      setHeight(window.innerWidth >= 1024 ? '42px' : '30px');
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);

    // Defer until the browser is idle so we don't eat into LCP / TBT.
    // Fallback to setTimeout for browsers without requestIdleCallback.
    const idle = (cb: () => void) => {
      const w = window as typeof window & { requestIdleCallback?: (cb: () => void) => number };
      if (typeof w.requestIdleCallback === 'function') {
        return w.requestIdleCallback(cb);
      }
      return window.setTimeout(cb, 200);
    };
    const handle = idle(() => setMounted(true));

    return () => {
      window.removeEventListener('resize', updateHeight);
      const w = window as typeof window & { cancelIdleCallback?: (id: number) => void };
      if (typeof w.cancelIdleCallback === 'function') {
        w.cancelIdleCallback(handle as number);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, []);

  if (!mounted) {
    return <div style={{ height }} aria-hidden="true" />;
  }

  const path = resolvedTheme === 'dark' ? '/lottie-dark-2.json' : '/lottie-light-1.json';
  return (
    <LottieAnimation
      height={height}
      path={path}
      className="w-full"
      slice={slice}
      speed={0.8}
      onReady={handleAnimationReady}
    />
  );
};

export default CardsLottie;
